import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Account } from '@flows.cash/types';
import { storage } from '../utils/storage';
import { Platform } from '../utils/platform';
import { supabase } from '../services/supabase';
import { useSupabaseSubscription } from '../hooks/useSupabaseSubscription';
import { useEffect, useState } from 'react';

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

interface AccountsState {
  accounts: Account[];
  loading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  fetchAccounts: (userId?: string) => Promise<Account[]>;
  addAccount: (userId: string, account: Omit<Account, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => Promise<string | null>;
  updateAccount: (userId: string, id: string, account: Partial<Account>) => Promise<void>;
  deleteAccount: (userId: string, id: string) => Promise<void>;
  clearAccounts: () => void;
  refreshAccountBalance: (userId: string, accountId: string) => Promise<number | null>;
  refreshAllBalances: (userId: string) => Promise<void>;
  fetchTotalBalance: (userId: string) => Promise<{ income: number; expenses: number; total: number; currency: string } | null>;
}

// Maximum time (in milliseconds) to consider cached accounts data as fresh
const CACHE_FRESHNESS_THRESHOLD = 5 * 60 * 1000; // 5 minutes

export const useAccountsStore = create<AccountsState>()(
  persist(
    (set, get) => ({
      accounts: [],
      loading: false,
      error: null,
      lastFetchTime: null,

      fetchAccounts: async (userId?: string): Promise<Account[]> => {
        if (!userId) {
          console.error('No userId provided for fetchAccounts');
          set({ error: 'No userId provided', loading: false });
          return [];
        }

        // Check if we have fresh cached data
        const { lastFetchTime, accounts } = get();
        const now = Date.now();
        const isCacheFresh = lastFetchTime && (now - lastFetchTime < CACHE_FRESHNESS_THRESHOLD);
        
        if (isCacheFresh && accounts.length > 0) {
          console.log(`Using cached accounts data (${accounts.length} accounts, fetched ${Math.round((now - lastFetchTime) / 1000)}s ago)`);
          return accounts;
        }

        set({ loading: true, error: null });
        
        try {
          console.log(`Fetching accounts for user ${userId}...`);
          
          // Check Supabase connection first
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Supabase session error:', sessionError);
            throw new Error(`Supabase session error: ${sessionError.message}`);
          }
          
          if (!sessionData.session) {
            console.warn('No active Supabase session found, this may cause authentication issues');
          }
          
          // Fetch accounts with server-calculated balances from account_balances_view
          const { data: accounts, error } = await supabase
            .from('account_balances_view')
            .select('*')
            .eq('user_id', userId)
            .order('account_name');

          if (error) {
            console.error('Error fetching accounts from account_balances_view:', error);
            throw error;
          }

          console.log(`Successfully fetched ${accounts?.length || 0} accounts with server-calculated balances`);
          
          // Format accounts data from the view
          const formattedAccounts = (accounts || []).map(account => ({
            id: account.account_id,
            userId: account.user_id,
            name: account.account_name,
            type: account.account_type || 'checking',
            balance: account.current_balance || 0, // Server-calculated balance from view
            currency: account.account_currency,
            color: account.account_color,
            icon: account.account_icon,
            isDefault: account.is_default || false,
            isArchived: account.is_archived || false,
            createdAt: account.created_at,
            updatedAt: account.updated_at
          })) as Account[];
          
          set({ 
            accounts: formattedAccounts, 
            loading: false,
            error: null,
            lastFetchTime: Date.now()
          });
          
          return formattedAccounts;
        } catch (error) {
          console.error('Error fetching accounts:', error);
          
          // If we have cached data, return it even if it's stale
          if (accounts.length > 0) {
            console.log(`Returning stale cached accounts data (${accounts.length} accounts) due to fetch error`);
            set({
              error: error instanceof Error ? error.message : 'Failed to fetch accounts',
              loading: false,
            });
            return accounts;
          }
          
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch accounts',
            loading: false,
          });
          return [];
        }
      },

      addAccount: async (userId: string, account: Omit<Account, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string | null> => {
        if (!userId) {
          console.error(' CoreAccountsStore: No userId provided for addAccount');
          set({ error: 'No userId provided' });
          return null;
        }

        set({ loading: true, error: null });
        
        try {
          // Check if this is the first account, if so make it default
          const { accounts } = get();
          const isFirstAccount = accounts.length === 0;
          
          // Create a minimal account object with only essential fields to avoid schema cache issues
          // Only include fields that are definitely in the database schema
          const newAccount: any = {
            user_id: userId,
            name: account.name,
            type: account.type,
            balance: account.balance,
            currency: account.currency,
            is_default: account.isDefault !== undefined ? account.isDefault : isFirstAccount
          };
          
          // Only add optional fields if they are defined
          if (account.color) newAccount.color = account.color;
          if (account.icon) newAccount.icon = account.icon;
          
          const { data, error } = await supabase
            .from('accounts')
            .insert([newAccount])
            .select()
            .single();

          if (error) {
            console.error(' CoreAccountsStore: Error inserting account:', error);
            throw error;
          }
          
          // If this is set as default, update other accounts to not be default
          if (newAccount.is_default) {
            await supabase
              .from('accounts')
              .update({ is_default: false })
              .eq('user_id', userId)
              .neq('id', data.id);
          }
          
          // Convert database fields back to TypeScript model fields
          const accountForState = {
            ...data,
            isDefault: data.is_default,
            isArchived: data.is_archived
          } as Account;
          
          set(state => ({
            accounts: [...state.accounts, accountForState],
            loading: false,
            lastFetchTime: Date.now()
          }));
          
          return data.id;
        } catch (error) {
          console.error(' CoreAccountsStore: Error adding account:', error);
          if (error instanceof Error) {
            console.error(' CoreAccountsStore: Error message:', error.message);
          }
          set({
            error: error instanceof Error ? error.message : 'Failed to add account',
            loading: false
          });
          return null;
        }
      },

      updateAccount: async (userId: string, id: string, accountUpdate: Partial<Account>): Promise<void> => {
        console.log(' CoreAccountsStore: updateAccount called with userId:', userId);
        console.log(' CoreAccountsStore: updateAccount called with id:', id);
        console.log(' CoreAccountsStore: updateAccount called with accountUpdate:', accountUpdate);
        
        if (!userId) {
          console.error(' CoreAccountsStore: No userId provided for updateAccount');
          set({ error: 'No userId provided' });
          return;
        }

        set({ loading: true, error: null });
        
        try {
          // Remove isArchived field if it exists and convert isDefault to is_default
          const { isArchived, isDefault, ...updateWithoutSpecialFields } = accountUpdate;
          
          // Prepare update data
          const updateData = {
            ...updateWithoutSpecialFields,
            // Add is_default if isDefault was provided
            ...(isDefault !== undefined ? { is_default: isDefault } : {}),
            updated_at: new Date().toISOString()
          };
          
          console.log(' CoreAccountsStore: Prepared update data:', updateData);
          
          // If setting this account as default
          if (isDefault) {
            console.log(' CoreAccountsStore: Setting all accounts to non-default first');
            // First, set all accounts to non-default
            const { error: resetError } = await supabase
              .from('accounts')
              .update({ is_default: false, updated_at: new Date().toISOString() })
              .eq('user_id', userId);
              
            if (resetError) {
              console.error(' CoreAccountsStore: Error resetting default accounts:', resetError);
              throw resetError;
            }
          }
          
          console.log(' CoreAccountsStore: Updating account in database');
          // Update the account
          const { error } = await supabase
            .from('accounts')
            .update(updateData)
            .eq('id', id)
            .eq('user_id', userId);

          if (error) {
            console.error(' CoreAccountsStore: Error updating account in database:', error);
            throw error;
          }
          
          console.log(' CoreAccountsStore: Account updated successfully in database');

          // Update local state
          set(state => ({
            accounts: state.accounts.map(account => 
              account.id === id 
                ? { ...account, ...accountUpdate, updatedAt: new Date().toISOString() } 
                : isDefault 
                  ? { ...account, isDefault: false } 
                  : account
            ),
            loading: false
          }));
        } catch (error) {
          console.error('Error updating account:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to update account',
            loading: false
          });
        }
      },

      deleteAccount: async (userId: string, id: string): Promise<void> => {
        if (!userId) {
          console.error('No userId provided for deleteAccount');
          set({ error: 'No userId provided' });
          return;
        }

        set({ loading: true, error: null });
        
        try {
          // Check if this is the default account
          const accountToDelete = get().accounts.find(a => a.id === id);
          if (accountToDelete?.isDefault) {
            // Find another account to make default
            const otherAccount = get().accounts.find(a => a.id !== id);
            if (otherAccount) {
              // Create an update with isDefault: true which will be converted to is_default: true
              await get().updateAccount(userId, otherAccount.id, { isDefault: true });
            }
          }
          
          // Delete the account
          const { error } = await supabase
            .from('accounts')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);

          if (error) throw error;
          
          // Update local state
          set(state => ({
            accounts: state.accounts.filter(account => account.id !== id),
            loading: false
          }));
        } catch (error) {
          console.error('Error deleting account:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to delete account',
            loading: false
          });
        }
      },

      clearAccounts: () => {
        set({ accounts: [], loading: false, error: null, lastFetchTime: null });
      },

      refreshAccountBalance: async (userId: string, accountId: string): Promise<number | null> => {
        if (!userId) {
          console.error('No userId provided for refreshAccountBalance');
          return null;
        }

        try {
          // Use the server-side account_balances_view to get accurate balance
          const { data, error } = await supabase
            .from('account_balances_view')
            .select('account_id, current_balance')
            .eq('user_id', userId)
            .eq('account_id', accountId)
            .single();

          if (error) throw error;
          
          if (!data) {
            console.error(`No balance data found for account ${accountId}`);
            return null;
          }
          
          // Extract the balance from the view data
          const balance = data.current_balance;
          
          // Update account balance in state
          set(state => ({
            accounts: state.accounts.map(account => 
              account.id === accountId 
                ? { ...account, balance } 
                : account
            )
          }));
          
          return balance;
        } catch (error) {
          console.error('Error refreshing account balance:', error);
          return null;
        }
      },

      refreshAllBalances: async (userId: string): Promise<void> => {
        if (!userId) {
          console.error('No userId provided for refreshAllBalances');
          return;
        }

        try {
          // Use the server-side account_balances_view to get all account balances at once
          const { data, error } = await supabase
            .from('account_balances_view')
            .select('account_id, current_balance')
            .eq('user_id', userId);

          if (error) throw error;
          
          if (!data || data.length === 0) {
            console.warn('No account balance data found');
            return;
          }
          
          // Update all account balances in state in a single operation
          set(state => ({
            accounts: state.accounts.map(account => {
              const balanceData = data.find(item => item.account_id === account.id);
              return balanceData 
                ? { ...account, balance: balanceData.current_balance } 
                : account;
            })
          }));
        } catch (error) {
          console.error('Error refreshing all balances:', error);
        }
      },

      fetchTotalBalance: async (userId: string): Promise<{ income: number; expenses: number; total: number; currency: string } | null> => {
        try {
          // Get user's preferred currency
          const { data: userData, error: userError } = await supabase
            .from('user_preferences')
            .select('currency')
            .eq('user_id', userId)
            .single();
          
          if (userError && userError.code !== 'PGRST116') {
            throw userError;
          }
          
          // Default to TRY if no preference is set
          const preferredCurrency = userData?.currency || 'TRY';
          
          // Get all account balances (income and expenses per account)
          const { data: accountBalances, error: accountError } = await supabase
            .from('account_income_expenses')
            .select('account_id, account_name, account_currency, income, expenses')
            .eq('user_id', userId);
          
          if (accountError) {
            throw accountError;
          }
          
          if (!accountBalances || accountBalances.length === 0) {
            return {
              income: 0,
              expenses: 0,
              total: 0,
              currency: preferredCurrency
            };
          }
          
          // Get all currencies used by accounts (filter out null/undefined values)
          const currencies = [...new Set(accountBalances
            .map(acc => acc.account_currency)
            .filter(currency => currency)
          )];
          
          // Only fetch exchange rates if we have accounts with different currencies
          let exchangeRates: any[] = [];
          
          if (currencies.some(c => c !== preferredCurrency)) {
            // Fetch ALL exchange rates with USD as base (this is how they're stored in DB)
            const { data: rates, error: ratesError } = await supabase
              .from('exchange_rates')
              .select('base_currency, target_currency, rate')
              .eq('base_currency', 'USD')
              .in('target_currency', [...currencies, preferredCurrency]);
            
            if (ratesError) {
              console.warn('Error fetching exchange rates:', ratesError);
            } else if (!rates || rates.length === 0) {
              console.warn('No exchange rates found for currencies:', currencies);
            } else {
              exchangeRates = rates;
            }
          }
          
          // Create a map of exchange rates (USD to target currency) for easy lookup
          const ratesMap: Record<string, number> = {};
          exchangeRates.forEach(rate => {
            if (rate.target_currency && rate.rate) {
              ratesMap[rate.target_currency] = Number(rate.rate);
            }
          });
          
          // Calculate totals with currency conversion at the account level
          let totalIncome = 0;
          let totalExpenses = 0;
          
          accountBalances.forEach(account => {
            // Convert amount to preferred currency if needed
            const accountCurrency = account.account_currency || preferredCurrency;
            
            if (accountCurrency !== preferredCurrency) {
              // Get rates for both currencies relative to USD
              const accountToUsdRate = ratesMap[accountCurrency] ? 1 / ratesMap[accountCurrency] : 1;
              const usdToPreferredRate = ratesMap[preferredCurrency] || 1;
              
              // Calculate conversion rate from account currency to preferred currency via USD
              const conversionRate = accountToUsdRate * usdToPreferredRate;
              
              // Apply conversion rate to account income and expenses
              const rawIncome = Number(account.income) || 0;
              const rawExpenses = Number(account.expenses) || 0;
              const convertedIncome = rawIncome * conversionRate;
              const convertedExpenses = rawExpenses * conversionRate;
              
              // Add to totals (ensure they are numbers)
              totalIncome += convertedIncome;
              totalExpenses += convertedExpenses;
            } else {
              // For accounts already in preferred currency, no conversion needed
              const rawIncome = Number(account.income) || 0;
              const rawExpenses = Number(account.expenses) || 0;
              
              totalIncome += rawIncome;
              totalExpenses += rawExpenses;
            }
          });
          
          // Ensure values are numbers and calculate balance correctly
          totalIncome = Number(totalIncome) || 0;
          totalExpenses = Number(totalExpenses) || 0;
          
          // Since expenses are already stored as negative values in the database,
          // we can directly add them to income to get the correct total
          const totalBalance = totalIncome + totalExpenses;
          
          return {
            income: totalIncome,
            // Return the absolute value of expenses for display purposes only
            expenses: Math.abs(totalExpenses),
            total: totalBalance,
            currency: preferredCurrency
          };
        } catch (error) {
          console.error('Error in fetchTotalBalance:', error);
          throw error;
        }
      },
    }),
    {
      name: 'flows-accounts-storage',
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({ 
        accounts: state.accounts,
        lastFetchTime: state.lastFetchTime
      }),
    }
  )
);

// Re-export the core store for direct access to accounts data
export const useAccounts = () => {
  const { accounts, loading, error } = useAccountsStore();
  return { accounts, loading, error };
};

export const { fetchAccounts, clearAccounts } = useAccountsStore.getState();

// Fix the useAccountsSubscription function
export function useAccountsSubscription() {
  const [userId, setUserId] = useState<string | null>(null);
  
  useEffect(() => {
    // Function to get the current user ID
    const fetchUserId = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user?.id) {
          setUserId(data.session.user.id);
        }
      } catch (error) {
        console.error('Error getting current user ID:', error);
      }
    };
    
    // Call the function
    fetchUserId();
  }, []);
  
  // Only subscribe if we have a userId
  useEffect(() => {
    if (!userId) return;
    
    // Let's use the original parameters that were in the code
    useSupabaseSubscription({
      table: 'accounts',
      filter: `user_id=eq.${userId}`,
      userId: userId,
      queryKeys: [['accounts', userId]],
      onRecordChange: () => {
        console.log('Accounts changed, refreshing data...');
        fetchAccounts(userId);
      }
    });
  }, [userId]);
  
  return userId;
}